import React, { FC, useRef, ChangeEvent, DragEventHandler } from 'react';
import { Box, FileItem, FileUploader, FormHelperText } from '@dxb-web-ui-framework/design-system';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { TFunction } from 'react-i18next';
import { mimeTypesToDownload } from '../../../utils/mimeTypesToDownload';

export type FileInputProps = {
  name: string;
  label: string;
  description: string;
  type: string;
  t: TFunction;
  supportedFiles: string[];
  mt?: number;
  selectedFile: Nullable<File>;
  setSelectedFile: Function;
  selectedFileError?: string;
  setSelectedFileError: Function;
  isLoading?: boolean;
  isDisabled?: boolean;
  fileNameValidationRegex?: RegExp;
};

const SingleFileUpload: FC<FileInputProps> = ({
  name,
  label,
  mt,
  description,
  type,
  supportedFiles,
  selectedFile,
  setSelectedFile,
  selectedFileError,
  setSelectedFileError,
  t,
  isDisabled,
  isLoading,
  fileNameValidationRegex,
}) => {
  const fileInputRef = useRef<Nullable<HTMLInputElement>>(null);

  const fileValidatorHandler = (file?: File): void => {
    if (!file) {
      return;
    }

    if (
      file.name &&
      !(
        supportedFiles.includes(file.type) ||
        (supportedFiles.includes(mimeTypesToDownload.cur) && file.name.endsWith('.cur') && file.type === '')
      )
    ) {
      setSelectedFileError(t('ContentComposer.FileUploader.FileType.Validation.Message'));
      return;
    }

    if (fileNameValidationRegex) {
      const fileNameWithoutExtension = file.name.split('.').slice(0, -1).join('.');
      const matches = fileNameWithoutExtension.match(fileNameValidationRegex);
      if (matches?.[0] !== fileNameWithoutExtension) {
        setSelectedFileError(t('ContentComposer.FileUploader.UnsupportedFileName.Validation.Message'));
        return;
      }
    }

    const fileSizeInMB = file.size / (1000 * 1000);
    if (fileSizeInMB > 5) {
      setSelectedFileError(t('ContentComposer.FileUploader.MaximumLimit.Validation.Message'));
      return;
    }
    setSelectedFile(file);
    setSelectedFileError(undefined);
  };

  const onClickFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const onDrop: DragEventHandler<HTMLElement> = event => {
    event.preventDefault();
    fileValidatorHandler(event.dataTransfer.files?.[0]);
  };

  const onDragOver: DragEventHandler<HTMLElement> = event => {
    event.preventDefault();
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    fileValidatorHandler(event.target.files?.[0]);
  };

  const onDeleteFile = () => {
    setSelectedFile(null);
  };

  return (
    <Box mt={mt}>
      <input id='fileInput' type='file' ref={fileInputRef} name={name} hidden={true} onChange={onChange} />
      <FileUploader
        description={description}
        label={label}
        type={type}
        loading={Boolean(isLoading)}
        disabled={Boolean(isDisabled)}
        onDrop={onDrop}
        onClick={onClickFileInput}
        onDragOver={onDragOver}>
        <>
          {selectedFile && !Boolean(isLoading) ? (
            <Box mt={1}>
              <FileItem
                filename={selectedFile.name}
                state={[{ type: 'close', onClick: onDeleteFile, title: t('FileItem.Delete.Action') }]}
              />
            </Box>
          ) : null}
          {selectedFileError && (
            <FormHelperText error={true} role='alert'>
              {selectedFileError}
            </FormHelperText>
          )}
        </>
      </FileUploader>
    </Box>
  );
};

export { SingleFileUpload };
